import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { isSpecialAccount as isSpecialAccountFromApi } from '../services/backend';

const SubscriptionContext = createContext();

export function useSubscription() {
  return useContext(SubscriptionContext);
}

export function SubscriptionProvider({ children }) {
  const { currentUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [specialAccount, setSpecialAccount] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      fetchSubscription();
      isSpecialAccountFromApi(currentUser.uid).then(setSpecialAccount);
    } else {
      setSubscription(null);
      setSpecialAccount(false);
      setLoading(false);
    }
  }, [currentUser]);

  const fetchSubscription = async () => {
    if (!currentUser) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setSubscription(userData.subscription || null);
      } else {
        // Create initial user document with trial subscription
        const trialSubscription = {
          status: 'trialing',
          plan: 'basic',
          trialEnd: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
          currentPeriodEnd: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          stripeCustomerId: null,
          stripeSubscriptionId: null
        };
        
        await setDoc(userRef, {
          email: currentUser.email,
          subscription: trialSubscription,
          createdAt: new Date().toISOString()
        });
        
        setSubscription(trialSubscription);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSubscription = async (subscriptionData) => {
    if (!currentUser) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        subscription: subscriptionData
      });
      setSubscription(subscriptionData);
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  };

  const isSpecialAccountUser = () => {
    return !!(currentUser && specialAccount);
  };

  const isTrialActive = () => {
    if (!subscription) return false;
    return subscription.status === 'trialing' && new Date(subscription.trialEnd) > new Date();
  };

  const isSubscriptionActive = () => {
    // Special accounts always have active subscription
    if (isSpecialAccountUser()) return true;
    
    if (!subscription) return false;
    return subscription.status === 'active' || isTrialActive();
  };

  const getDaysLeftInTrial = () => {
    if (!isTrialActive()) return 0;
    const trialEnd = new Date(subscription.trialEnd);
    const now = new Date();
    const diffTime = trialEnd - now;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getSubscriptionStatus = () => {
    if (isSpecialAccountUser()) {
      return {
        status: 'special',
        plan: 'premium',
        isActive: true,
        daysLeft: null,
        message: 'Special Account - Full Access'
      };
    }
    
    if (isTrialActive()) {
      return {
        status: 'trialing',
        plan: 'basic',
        isActive: true,
        daysLeft: getDaysLeftInTrial(),
        message: `${getDaysLeftInTrial()} days left in trial`
      };
    }
    
    if (subscription?.status === 'active') {
      return {
        status: 'active',
        plan: 'basic',
        isActive: true,
        daysLeft: null,
        message: 'Active subscription'
      };
    }
    
    return {
      status: 'inactive',
      plan: 'none',
      isActive: false,
      daysLeft: 0,
      message: 'No active subscription'
    };
  };

  const value = {
    subscription,
    loading,
    updateSubscription,
    isTrialActive,
    isSubscriptionActive,
    getDaysLeftInTrial,
    fetchSubscription,
    isSpecialAccount: isSpecialAccountUser,
    getSubscriptionStatus
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
} 